// External Dependencies
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { Center } from '@chakra-ui/react';
import { DisplayIndividualQuestionDto } from '@keyops-cep/api-client';
import { Chart } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Internal Dependencies
import useFilterProvider from '../../../HOCs/Filter/useFilterProvider';
import { AnswerDataWithDemographic } from '../../../pages/EngagementDetails/types';
import { CHART_COLORS } from '../../../utils/constants/visualization-constants';
import { getBreakoutDemographicsDatasets } from '../../../utils/functions/breakout-demographics-datasets';
import {
  doesLabelFitInBar,
  getBarDimensions,
  getChartTitleConfig,
  getQuestionGraphFontSize,
} from '../../../utils/functions/chart-label-utils';
import {
  calculateRange,
  convertToCountsObject,
  Counts,
} from '../../../utils/functions/stats';
import {
  copyGraph,
  downloadGraph,
} from '../../../utils/functions/visualization-actions';
import { getPrevAvgAnnotation } from '../../../utils/functions/visualization-utils';
import {
  MarkerFont,
  MarkerPluginType,
  prevAverageMarkerPlugin,
} from '../ChartCard/chartPlugins';
import { VisualizationChartRef } from '..';

type HistogramProps = {
  question: DisplayIndividualQuestionDto;
  questionAnswers: AnswerDataWithDemographic[];
  scaleType?: 'linear';
  maxValue?: number | 'fromData';
  minValue?: number | 'fromData';
  xAxisLabel?: string;
  yAxisLabel?: string;
  averageMarkerPlugin?: (
    counts: Counts,
    rangeValue: number,
    font: MarkerFont
  ) => MarkerPluginType;
  displayPrevAvg?: boolean;
  maintainAspectRatio?: boolean;
  isExpanded?: boolean;
};

// This is the limit of characters for the label. If the labels get too long,
// the visualization can get messed up.
const labelLimit = 15;
const dataLabelLimit = 7;

const Histogram = forwardRef<VisualizationChartRef, HistogramProps>(
  (
    {
      question,
      questionAnswers,
      scaleType,
      maxValue,
      minValue,
      xAxisLabel = question.displayConfig?.xAxisTitle,
      yAxisLabel = question.displayConfig?.yAxisTitle,
      averageMarkerPlugin,
      displayPrevAvg,
      maintainAspectRatio = false,
      isExpanded = false,
    },
    ref
  ) => {
    const { DEFAULT_FILTER_GROUPS, breakoutDemographic } = useFilterProvider();
    const font = getQuestionGraphFontSize(isExpanded);

    const rangeValue = useMemo(
      () => calculateRange(question.type, questionAnswers),
      [question.type, questionAnswers]
    );

    const breakoutDemographicsDatasets = useMemo(() => {
      return getBreakoutDemographicsDatasets(
        breakoutDemographic,
        DEFAULT_FILTER_GROUPS,
        questionAnswers,
        question,
        rangeValue
      );
    }, [
      questionAnswers,
      question,
      DEFAULT_FILTER_GROUPS,
      breakoutDemographic,
      rangeValue,
    ]);

    // Get the raw count data (without aggregating by rangeValue) for calculating the average
    const avgCalc = useMemo(
      () =>
        convertToCountsObject(
          questionAnswers,
          question.displayConfig?.orderBy,
          question.visualization,
          question.type
        ),
      [
        questionAnswers,
        question.visualization,
        question.type,
        question.displayConfig?.orderBy,
      ]
    );

    // get latest previous average from annotations, if it is available
    const prevAvgAnnotation = getPrevAvgAnnotation(question);

    const { counts, convertedToRanges } = useMemo(
      () =>
        convertToCountsObject(
          questionAnswers,
          question.displayConfig?.orderBy,
          question.visualization,
          question.type,
          rangeValue
        ),
      [
        questionAnswers,
        question.visualization,
        question.type,
        rangeValue,
        question.displayConfig?.orderBy,
      ]
    );
    const showLabelDetailView = Object.keys(counts).length <= dataLabelLimit;

    const { maxCount, maxX, minX, xStepSize } = useMemo(() => {
      let maxCount;
      // if breakoutDemographics we need to find the maxCount within array of demos
      if (breakoutDemographicsDatasets.length > 0) {
        let breakoutMax = 0;
        breakoutDemographicsDatasets.forEach((demo) => {
          const demoMax = Math.max(...Object.values(demo.data));
          if (demoMax > breakoutMax) breakoutMax = demoMax;
        });
        maxCount = breakoutMax;
      } else {
        maxCount = Object.keys(counts).reduce((currentMax, key) => {
          if (currentMax < counts[key]) {
            return counts[key];
          } else {
            return currentMax;
          }
        }, 0);
      }

      const maxX =
        maxValue === 'fromData'
          ? Object.keys(counts).reduce((currentMax, key) => {
              const keyAsInt = parseInt(key);
              if (!isNaN(keyAsInt) && currentMax < keyAsInt) {
                return keyAsInt;
              } else {
                return currentMax;
              }
            }, 0)
          : maxValue;
      const minX =
        minValue === 'fromData'
          ? Object.keys(counts).reduce((currentMin, key) => {
              const keyAsInt = parseInt(key);
              if (!isNaN(keyAsInt) && currentMin > keyAsInt) {
                return keyAsInt;
              } else {
                return currentMin;
              }
            }, 0)
          : minValue;
      const xStepSize = maxX
        ? maxX >= 20 && maxX < 70
          ? 5
          : maxX > 70
          ? 10
          : 1
        : 1;

      return { maxCount, maxX, minX, xStepSize };
    }, [counts, maxValue, minValue, breakoutDemographicsDatasets]);

    const chartRef: { current: Chart<'bar', Counts> | null } = useRef(null);
    const chartContainer = useRef<HTMLCanvasElement>(null);

    useImperativeHandle(ref, () => ({
      onDownload: (name: string) => {
        downloadGraph(name, chartRef);
      },

      onCopyToClipboard: async () => {
        await copyGraph(chartRef);
      },
    }));

    useEffect(() => {
      if (chartContainer.current) {
        chartRef.current = new Chart<'bar', Counts>(chartContainer.current, {
          type: 'bar',
          data: {
            datasets: breakoutDemographic
              ? breakoutDemographicsDatasets.map((dataset) => ({
                  ...dataset,
                  borderRadius: (ctx) => {
                    const { width } = getBarDimensions(ctx);
                    return Math.min(
                      Math.floor(width * 0.3),
                      isExpanded ? 10 : 5
                    );
                  },
                }))
              : [
                  {
                    data: counts,
                    backgroundColor: CHART_COLORS[2],
                    borderRadius: (ctx) => {
                      const { width } = getBarDimensions(ctx);
                      return Math.min(
                        Math.floor(width * 0.3),
                        isExpanded ? 10 : 5
                      );
                    },
                    categoryPercentage: 0.9, // closer to 1 for less space between categories
                    barPercentage: 1, // closer to 1 for wider bars within their category
                    maxBarThickness: isExpanded ? 100 : 50, // limit bar width
                  },
                ],
          },
          options: {
            responsive: true,
            maintainAspectRatio,
            layout: {
              padding: {
                // Prevent average annotation from getting clipped
                top: averageMarkerPlugin
                  ? prevAvgAnnotation
                    ? 55
                    : 43
                  : showLabelDetailView
                  ? 20
                  : 0,
                right: 0,
                bottom: 0,
                left: 0,
              },
            },
            plugins: {
              legend: {
                display: breakoutDemographic ? true : false,
                position: 'bottom',
                labels: {
                  boxWidth: Chart.defaults.font.size,
                  font: getQuestionGraphFontSize(isExpanded, true),
                },
                onClick: (e, legendItem, legend) => {
                  e.native?.preventDefault();
                },
              },
              datalabels: {
                anchor: (context) => {
                  return doesLabelFitInBar(context, 24, 30) ? 'start' : 'end';
                },
                align: 'end',
                color: (context) => {
                  return doesLabelFitInBar(context, 24, 30) ? 'white' : 'black';
                },
                font: (context) => {
                  const { width } = getBarDimensions(context);
                  const size = Math.max(
                    10, // 10px minimum font size
                    Math.min(Math.floor(width * 0.8), font.size)
                  );
                  return {
                    size,
                    weight: 'bold',
                  };
                },
                formatter: (_, context) => {
                  const keys = Object.keys(context.dataset.data);
                  const index = keys[context.dataIndex];
                  const newValue =
                    context.dataset.data[
                      index as keyof typeof context.dataset.data
                    ];
                  if (newValue === 0) return null;
                  return newValue;
                },
              },
              ...(question.displayConfig?.chartTitle && {
                title: getChartTitleConfig(
                  question.displayConfig?.chartTitle,
                  isExpanded
                ),
              }),
            },
            scales: {
              x: {
                ...(xAxisLabel && {
                  title: {
                    display: isExpanded,
                    text: xAxisLabel,
                    font,
                  },
                }),
                ...(!convertedToRanges && scaleType && { type: scaleType }),
                ...(!convertedToRanges && maxX && { max: maxX }),
                ...(!convertedToRanges && minX && { min: minX }),
                ticks: {
                  font,
                  stepSize: xStepSize,
                  callback: function (value) {
                    if (value || value === 0) {
                      const label: string = this.getLabelForValue(
                        parseInt(value as string)
                      );
                      return label.length > labelLimit
                        ? `${label.substring(0, labelLimit)}...`
                        : label;
                    }
                    return '';
                  },
                },
                grid: {
                  display: false,
                },
              },
              y: {
                ticks: {
                  stepSize: Math.max(Math.round(maxCount / 5), 1),
                  font,
                },
                ...(yAxisLabel && {
                  title: {
                    display: isExpanded,
                    text: yAxisLabel,
                    font,
                    padding: {
                      bottom: question.displayConfig?.yAxisTitle ? 20 : 0,
                    },
                  },
                }),
                grid: {
                  color: 'rgba(0, 0, 0, 0.05)',
                },
                grace: 0,
              },
            },
          },
          plugins: [
            ...(averageMarkerPlugin
              ? [averageMarkerPlugin(avgCalc.counts, rangeValue, font)]
              : []),
            ...(displayPrevAvg && !!prevAvgAnnotation
              ? [
                  prevAverageMarkerPlugin(
                    prevAvgAnnotation.value,
                    prevAvgAnnotation.description,
                    rangeValue,
                    font
                  ),
                ]
              : []),

            ...(isExpanded || showLabelDetailView ? [ChartDataLabels] : []),
          ],
        });
      }

      // Clean up the chart instance when the component unmounts
      return () => {
        if (chartRef.current) {
          chartRef.current.destroy();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [counts, displayPrevAvg, breakoutDemographic, question]);

    return (
      <Center h={'100%'} pt={averageMarkerPlugin ? 6 : 0}>
        <canvas
          id={`chart-${Math.floor(Math.random() * 100)}`}
          ref={chartContainer}
        ></canvas>
      </Center>
    );
  }
);

Histogram.displayName = 'Histogram';

export default Histogram;
